/**
 * ImageGeneratorNode (Canvas V2 — Sprint 9i migration)
 *
 * Migrated to UnifiedBottomPanel with 3 tabs: Config, Refine, Variants.
 *
 * Changes from pre-migration:
 * - Removed inline config section (renderConfig) — all config lives in Config tab
 * - Removed ImageHistoryPopover — Variants tab replaces it
 * - Removed HistoryBadgeButton — no longer needed
 * - Both standalone and group mode use UnifiedBottomPanel
 * - Card shows only the image display area + toolbar
 * - Width adapts to aspect ratio (existing ASPECT_WIDTH_MAP)
 */

import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow'
import { BookmarkPlus } from 'lucide-react'
import { getThumbnailUrl, getAssetUrl } from '../../../../api/useAssetUrl'
import { executeNode } from '../../../../api/client'
import { useCanvasOptional } from '../../CanvasContext'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  UnifiedBottomPanel,
  IMAGE_GEN_TABS,
  DEFAULT_TAB,
  ConfigTabContent,
  RefineTabContent,
  VariantsTabContent,
  useNodeActions,
  useBottomPanelAutoOpen,
  ASPECT_RATIO_MAP,
  ASPECT_WIDTH_MAP,
  type ToolbarAction,
  type SelectedReference,
  buildAssetCandidates,
} from '../shared'
import type { HistoryEntry } from '../shared/types'

// ─── Constants ────────────────────────────────────────────────────────────────

const ASPECT_RATIO_OPTIONS = ['16:9', '9:16', '3:4', '4:3', '1:1'] as const

/** Default style */
const DEFAULT_STYLE = '2d-comic'

// ─── Data shape ───────────────────────────────────────────────────────────────

import { useProjectId } from '../../../../contexts/ProjectIdContext'
import type { ImageGeneratorNodeData } from '../../../../generated/nodeDataTypes'

export type { ImageGeneratorNodeData }

// ─── Component ────────────────────────────────────────────────────────────────

const ImageGeneratorNode = memo((props: NodeProps<ImageGeneratorNodeData>) => {
  const { id, data, selected, dragging } = props
  const {
    cacheKey,
    out_generated_image,
    out_image_path,
    in_name,
    in_prompt = '',
    in_aspect_ratio = '16:9',
    in_style = DEFAULT_STYLE,
    in_quality = '1K',
    in_reference_images,
    status = 'idle',
    progressMessage,
    error,
  } = data
  const projectId = useProjectId()

  const { setNodes, getNodes } = useReactFlow()
  const canvas = useCanvasOptional()
  const { handleDelete, handleDuplicate, handleDownload, handleSaveToLibrary } = useNodeActions({ nodeId: id })

  const cssAspectRatio = ASPECT_RATIO_MAP[in_aspect_ratio] ?? '16/9'
  const nodeWidth = ASPECT_WIDTH_MAP[in_aspect_ratio] ?? 315

  // Build image URL from out_generated_image history
  let imagePath: string | undefined
  if (out_generated_image?.history?.length) {
    const idx = out_generated_image.picked_index ?? 0
    imagePath = out_generated_image.history[idx]?.result?.local_path
  }
  if (!imagePath && out_image_path) {
    imagePath = out_image_path
  }

  const cacheBust = cacheKey
  const imageUrl =
    imagePath && projectId
      ? getThumbnailUrl(projectId, imagePath, 0, cacheBust)
      : null
  const fullImageUrl =
    imagePath && projectId
      ? getAssetUrl(projectId, imagePath, 0, cacheBust)
      : null

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const spinClass = isRunning ? 'animate-spin' : isPendingCost ? '' : 'animate-spin-slow'
  const isError = status === 'error'

  // ── History ────────────────────────────────────────────────────────────────

  const historyEntries = useMemo(
    () => {
      if (out_generated_image?.history?.length) {
        return out_generated_image.history as HistoryEntry[]
      }
      return [] as HistoryEntry[]
    },
    [out_generated_image?.history]
  )
  const pickedIndex = out_generated_image?.picked_index ?? 0
  const batchPending = isRunning ? (data.batch_pending ?? 0) : 0

  // Auto-advance picked_index to latest on new generation
  useEffect(() => {
    if (historyEntries.length > 0) {
      const latestIdx = historyEntries.length - 1
      if (pickedIndex < latestIdx) {
        const entry = historyEntries[latestIdx]
        const localPath = entry?.result?.local_path
        if (localPath) {
          const newCacheKey = String(Date.now())
          const updatedGenImage = { ...out_generated_image, picked_index: latestIdx }
          canvas?.updateNodeData(id, {
            out_generated_image: updatedGenImage,
            out_image_path: localPath,
            cacheKey: newCacheKey,
          })
        }
      }
    }
  }, [historyEntries.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleHistorySelect = useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      const localPath = entry?.result?.local_path
      if (!localPath) return

      const currentNode = getNodes().find((n) => n.id === id)
      const currentGenImage = currentNode?.data?.out_generated_image
      const newCacheKey = String(Date.now())
      const updatedGenImage = { ...currentGenImage, picked_index: index }

      setNodes((nds) =>
        nds.map((n) =>
          n.id !== id
            ? n
            : {
                ...n,
                data: {
                  ...n.data,
                  out_generated_image: updatedGenImage,
                  out_image_path: localPath,
                  cacheKey: newCacheKey,
                },
              }
        )
      )
      canvas?.updateNodeData(id, {
        out_generated_image: updatedGenImage,
        out_image_path: localPath,
        cacheKey: newCacheKey,
      })
    },
    [id, historyEntries, getNodes, setNodes, canvas]
  )

  // ── Bottom panel state ────────────────────────────────────────────────────

  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      setNodes((nodes) => nodes.map((n) => ({ ...n, selected: n.id === id })))
    },
    [id, setNodes]
  )

  // ── Lightbox ───────────────────────────────────────────────────────────────

  const [isModalOpen, setIsModalOpen] = useState(false)

  // ── Deferred aspect ratio ─────────────────────────────────────────────────
  // Changing ratio in flyout stores a pending value. Node card retains current
  // image dimensions. New ratio applies on next generation only.
  const [pendingAspectRatio, setPendingAspectRatio] = useState<string | null>(null)

  /** The ratio shown in config UI (pending if set, otherwise current) */
  const displayAspectRatio = pendingAspectRatio ?? in_aspect_ratio

  // ── Config updates ─────────────────────────────────────────────────────────

  const updateConfig = useCallback(
    (key: string, value: string | string[] | undefined) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [key]: value } } : n))
      )
      canvas?.updateNodeData(id, { [key]: value })
    },
    [id, setNodes, canvas]
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      updateConfig('in_name', newName)
    },
    [updateConfig],
  )

  // ── Generate handler ───────────────────────────────────────────────────────

  const handleGenerate = useCallback(async (retryCount: number = 1) => {
    // Guard: double-submit or pending_execution/pending_cost_confirmation (orchestrator will fire it)
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') return

    // Guard: missing projectId (no silent failure)
    if (!projectId) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: 'Missing projectId.' } }
            : n
        )
      )
      return
    }

    // Apply pending aspect ratio before generation
    if (pendingAspectRatio) {
      updateConfig('in_aspect_ratio', pendingAspectRatio)
      setPendingAspectRatio(null)
    }

    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? { ...n, data: { ...n.data, status: 'running', progress: 0, error: undefined } }
          : n
      )
    )

    try {
      await executeNode(
        projectId,
        id,
        undefined,
        retryCount
      )
    } catch (err) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
            : n
        )
      )
    }
  }, [projectId, id, status, setNodes, pendingAspectRatio, updateConfig])

  // ── Download handler ───────────────────────────────────────────────────────

  const onDownload = useCallback(() => {
    if (fullImageUrl) {
      handleDownload(fullImageUrl, imagePath?.split('/').pop() || 'generated.jpg')
    }
  }, [fullImageUrl, imagePath, handleDownload])

  // ── Refinement handler ────────────────────────────────────────────────────

  const [feedbackText, setFeedbackText] = useState('')

  const handleRefineSubmit = useCallback(async () => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation' || !projectId) return

    // Apply pending aspect ratio before generation
    if (pendingAspectRatio) {
      updateConfig('in_aspect_ratio', pendingAspectRatio)
      setPendingAspectRatio(null)
    }

    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                status: 'running',
                progressMessage: 'Refining image...',
                error: undefined,
              },
            }
          : n
      )
    )

    try {
      // Write feedback to YDoc before executing
      if (feedbackText.trim()) {
        canvas?.updateNodeData(id, { in_feedback: feedbackText.trim() })
      }
      await executeNode(projectId, id)
      setFeedbackText('')
    } catch (err) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
            : n
        )
      )
    }
  }, [projectId, id, feedbackText, status, setNodes, pendingAspectRatio, updateConfig, canvas])

  // ── Reference handling for ConfigTabContent ───────────────────────────────

  const currentRefs = useMemo<SelectedReference[]>(() => {
    if (!in_reference_images?.length) return []
    const candidates = buildAssetCandidates(getNodes(), id)
    const pathToCandidate = new Map(candidates.map((c) => [c.assetPath, c]))
    return in_reference_images.map((path, i) => {
      const match = pathToCandidate.get(path)
      return {
        nodeId: match?.nodeId ?? `ref-${i}`,
        nodeType: match?.nodeType ?? 'image',
        label: match?.label ?? path.split('/').pop() ?? `Reference ${i + 1}`,
        assetPath: path,
        mediaType: (match?.mediaType ?? 'image') as 'image' | 'video',
      }
    })
  }, [in_reference_images, getNodes, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      const paths = refs.map((r) => r.assetPath)
      updateConfig('in_reference_images', paths.length > 0 ? paths : undefined)
    },
    [updateConfig]
  )

  // ── Toolbar actions ────────────────────────────────────────────────────────

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'expand',
        label: 'Expand',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
            />
          </svg>
        ),
        onClick: () => setIsModalOpen(true),
        hidden: !imageUrl,
        group: true,
      },
      {
        key: 'download',
        label: 'Download',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        ),
        onClick: onDownload,
        hidden: !imageUrl,
      },
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger' as const,
      },
    ],
    [imageUrl, onDownload, handleDuplicate, handleDelete, handleSaveToLibrary]
  )

  // ── Panel visibility ──────────────────────────────────────────────────────

  const panelVisible = !!selected && !dragging && bottomPanelOpen && (!showSpinner || batchPending > 0)

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="relative">
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

        {/* Floating label above the card */}
        <FloatingNodeLabel name={in_name || ''} fallback="Image Generator" onRename={handleRename} />

        <div
          className={`rounded-lg overflow-hidden transition-all duration-150 ${showSpinner ? 'border-2 animate-border-glow' : ''}`}
          style={{
            width: nodeWidth,
            boxShadow: selected
              ? '0 0 0 2px rgba(255,255,255,0.7)'
              : isError
                ? '0 0 0 1px rgba(239,68,68,0.5)'
                : showSpinner
                  ? '0 0 0 1px rgba(234,179,8,0.5)'
                  : '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          {/* Handles */}
          <Handle
            type="target"
            position={Position.Top}
            id="in_upstream"
            className="!bg-transparent !border-0 !w-4 !h-1"
          />
          <Handle
            type="target"
            position={Position.Left}
            id="in_reference_images"
            className="!bg-transparent !border-0 !w-1 !h-4"
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="out_generated_image"
            className="!bg-transparent !border-0 !w-4 !h-1"
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="out_image_path"
            className="!bg-transparent !border-0 !w-0 !h-0"
          />

          {/* Main content area — all 4 corners rounded */}
          <div className="rounded-lg overflow-hidden">
            {showSpinner ? (
              /* Generating / queued state */
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center px-4"
                style={{ aspectRatio: cssAspectRatio }}
              >
                <div className={`w-6 h-6 border-2 border-[var(--warning)] border-t-transparent rounded-full ${spinClass}`} />
                <span className="text-xxs text-foreground/50 mt-2 text-center leading-relaxed">
                  {isRunning
                    ? (progressMessage || 'Image Generator is creating your image now')
                    : isPendingCost
                      ? 'Waiting for cost approval in chat...'
                      : 'Queued — waiting for upstream nodes...'}
                </span>
                {isRunning && (
                  <span className="text-xxs text-foreground/25 mt-1">
                    This may take up to 20 seconds
                  </span>
                )}
              </div>
            ) : imageUrl ? (
              /* Complete state — generated image */
              <div
                className="relative group"
                style={{ aspectRatio: cssAspectRatio, overflow: 'hidden' }}
              >
                <img
                  src={imageUrl}
                  alt="Generated"
                  className={`w-full h-full object-cover block transition-transform duration-300 ease-out ${
                    !selected ? 'group-hover:scale-[1.02]' : ''
                  }`}
                />

                {/* Overlay controls */}
                <div
                  className="absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsModalOpen(true)
                    }}
                    className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                    title="Expand"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDownload()
                    }}
                    className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                    title="Download"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ) : isError && error ? (
              /* Error state */
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center px-6"
                style={{ aspectRatio: cssAspectRatio }}
              >
                <svg
                  className="w-8 h-8 text-red-400/60"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
                <span
                  className="text-xxs text-red-400/80 mt-1.5 text-center line-clamp-2 cursor-help"
                  title={error}
                >
                  {error}
                </span>
                <span className="text-xxs text-foreground/20 mt-1">Click to retry</span>
              </div>
            ) : (
              /* Empty placeholder */
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center"
                style={{ aspectRatio: cssAspectRatio }}
              >
                <svg
                  className="w-8 h-8 text-foreground/10"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-xxs text-foreground/20 mt-1">Image Generator</span>
              </div>
            )}
          </div>

          {/* Quick Info Bar (hidden while running/queued) — 3 columns: Config | Variants | Reference */}
          {!showSpinner && (
            <div
              data-testid="quick-info-bar"
              className="grid grid-cols-3 border-t border-border-subtle bg-surface-0"
            >
              {/* Config Column */}
              <button
                data-testid="quick-info-config"
                onClick={(e) => {
                  e.stopPropagation()
                  openPanelToTab('config')
                }}
                className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle"
              >
                <span className="text-[10px] text-muted-foreground leading-none">Config</span>
                <span className="text-xs text-muted-foreground truncate">
                  {displayAspectRatio} · {in_quality}
                </span>
              </button>

              {/* Variants Column */}
              <button
                data-testid="quick-info-variants"
                onClick={(e) => {
                  e.stopPropagation()
                  openPanelToTab('variants')
                }}
                className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle"
              >
                <span className="text-[10px] text-muted-foreground leading-none">Variants</span>
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        historyEntries.length > 0 ? 'var(--primary)' : 'var(--text-muted)',
                    }}
                  />
                  <span className="text-xs font-medium text-foreground">
                    {historyEntries.length}
                  </span>
                </div>
              </button>

              {/* Reference Column */}
              <button
                data-testid="quick-info-reference"
                onClick={(e) => {
                  e.stopPropagation()
                  openPanelToTab('config')
                }}
                className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag"
              >
                <span className="text-[10px] text-muted-foreground leading-none">Reference</span>
                <div className="flex items-center gap-1">
                  {(in_reference_images || []).slice(0, 2).map((path, i) => {
                    const thumbUrl = projectId
                      ? getThumbnailUrl(projectId, path, 32, cacheBust)
                      : null
                    return thumbUrl ? (
                      <div key={i} className="w-6 h-6 rounded overflow-hidden border border-border">
                        <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : null
                  })}
                  {!(in_reference_images?.length) && (
                    <span className="text-xs text-text-muted/40">--</span>
                  )}
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Unified Bottom Panel (both standalone and group mode) */}
        <UnifiedBottomPanel
          tabs={IMAGE_GEN_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isVisible={panelVisible}
        >
          {(tab) => {
            if (tab === 'config' && projectId) {
              return (
                <ConfigTabContent
                  nodeId={id}
                  nodeType="imageGenerator"
                  prompt={in_prompt}
                  onPromptChange={(value) => updateConfig('in_prompt', value)}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  currentStyleId={in_style}
                  onStyleChange={(styleId) => updateConfig('in_style', styleId)}
                  aspectRatio={displayAspectRatio}
                  quality={in_quality}
                  onAspectRatioChange={(ratio) => setPendingAspectRatio(ratio)}
                  onQualityChange={(quality) => updateConfig('in_quality', quality)}
                  isRunning={isRunning}
                  onGenerate={handleGenerate}
                />
              )
            }
            if (tab === 'refine') {
              return (
                <RefineTabContent
                  feedbackText={feedbackText}
                  onFeedbackTextChange={setFeedbackText}
                  onSubmit={handleRefineSubmit}
                  isRunning={isRunning}
                  nodeType="imageGenerator"
                  selectedStyleId={in_style}
                  onStyleChange={(styleId) => updateConfig('in_style', styleId)}
                />
              )
            }
            if (tab === 'variants') {
              return (
                <VariantsTabContent
                  history={historyEntries}
                  pickedIndex={pickedIndex}
                  onSelectVariant={handleHistorySelect}
                  batchPending={batchPending}
                  cacheKey={cacheBust}
                  projectId={projectId}
                  accentColor="purple"
                  aspectRatio={cssAspectRatio}
                />
              )
            }
            return null
          }}
        </UnifiedBottomPanel>
      </div>

      {/* Fullscreen lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt="Full size"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

ImageGeneratorNode.displayName = 'ImageGeneratorNode'
export default ImageGeneratorNode
export { ASPECT_RATIO_OPTIONS }
